import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useTheme } from '../../../contexts/ThemeContext';

export interface ActionButtonProps {
  onClick: () => void;
  icon: string;
  label: string;
  className?: string;
  isActive?: boolean;
  showTooltip?: boolean;
  tooltipText?: string;
  size?: 'icon' | 'extended';
  disabled?: boolean;
  'aria-label'?: string;
  tooltipDuration?: number; // Duration in ms before tooltip auto-dismisses
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  icon,
  label,
  className,
  isActive,
  showTooltip,
  tooltipText,
  size = 'extended',
  disabled,
  'aria-label': ariaLabel,
  tooltipDuration = 2000, // Default 3 seconds
}) => {
  const { theme } = useTheme();
  const isExtended = size === 'extended';
  const [showIconTooltip, setShowIconTooltip] = useState(false);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  // Show tooltip for icon-only buttons or when explicitly requested
  const shouldShowTooltip = (!isExtended && showIconTooltip) || (showTooltip && tooltipText);
  const tooltipContent = tooltipText || label;

  // Set up auto-dismiss timer for mobile
  useEffect(() => {
    // Clear any existing timer when tooltip state changes
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }

    // If tooltip is shown on mobile, set timer to hide it
    if (shouldShowTooltip && isMobile) {
      tooltipTimerRef.current = setTimeout(() => {
        setShowIconTooltip(false);
      }, tooltipDuration);
    }

    // Cleanup on unmount
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, [shouldShowTooltip, isMobile, tooltipDuration]);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || label}
      onMouseEnter={() => !isExtended && setShowIconTooltip(true)}
      onMouseLeave={() => !isExtended && setShowIconTooltip(false)}
      onTouchStart={() => {
        if (!isExtended) {
          setShowIconTooltip(true);
        }
      }}
      className={`
        relative shrink-0 inline-flex items-center justify-center gap-2 
        h-10 rounded-full
        text-sm font-medium
        transition-all duration-200 
        border border-border-primary border-1
        bg-surface-secondary
        hover:bg-surface-tertiary active:bg-surface-tertiary
        disabled:opacity-50 disabled:cursor-not-allowed
        ${theme.mode === "dark" ? "text-text-primary" : "text-text-primary"}
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50
        group
        ${isExtended ? 'px-4' : 'w-10'}
        ${className || ''}
      `}
    >
      <Icon
        icon={icon}
        className={`w-5 h-5 transition-transform group-hover:scale-110 ${
          isActive ? "text-primary-500" : ""
        }`}
      />
      {isExtended && <span>{label}</span>}
      {shouldShowTooltip && (
        <div className="
          absolute -top-12 left-1/2 -translate-x-1/2 
          px-4 py-2 rounded-lg 
          bg-black/90 text-white text-sm 
          whitespace-nowrap shadow-lg
          animate-fade-in z-50
        ">
          {tooltipContent}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/90 rotate-45" />
        </div>
      )}
    </button>
  );
}; 